
import { LayoutGrid, Server, Code, ArrowRight } from "lucide-react";
import { ContactButton } from "@/components/ui/button";

interface ServiceCardProps {
  icon: React.ElementType;
  title: string;
  description: string;
  modalTitle?: string;
  modalDescription?: string;
  iconBgColor?: string;
  iconColor?: string;
}

const ServiceCard = ({ 
  icon: Icon, 
  title, 
  description, 
  modalTitle, 
  modalDescription,
  iconBgColor = "bg-purple-50",
  iconColor = "text-edgefi-purple" 
}: ServiceCardProps) => {
  return (
    <div className="p-8 rounded-xl bg-white border border-gray-100 shadow-sm hover:shadow-md transition-all duration-300 h-full flex flex-col">
      <div className={`${iconBgColor} p-3 rounded-lg inline-flex mb-5 w-fit`}>
        <Icon className={`h-7 w-7 ${iconColor}`} aria-hidden="true" />
      </div>
      <h3 className="text-xl font-semibold mb-3 text-gray-900">{title}</h3>
      <p className="text-gray-600 mb-6 flex-grow">{description}</p>
      <div className="mt-auto">
        <ContactButton 
          variant="outline" 
          className="w-full group border-edgefi-purple/30 hover:border-edgefi-purple text-edgefi-purple"
          modalTitle={modalTitle}
          modalDescription={modalDescription}
        >
          Learn More 
          <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" aria-hidden="true" />
        </ContactButton>
      </div>
    </div>
  );
};

export const ServiceCards = () => {
  const services = [
    {
      icon: LayoutGrid,
      title: "M365 Architecture Design",
      description: "Custom Microsoft 365 architecture designs that align with your business objectives, security requirements, and user needs.",
      modalTitle: "M365 Architecture Design",
      modalDescription: "Our Microsoft 365 architecture design services create custom solutions aligned with your business needs. Fill out the form below to learn more.",
      iconBgColor: "bg-purple-50",
      iconColor: "text-edgefi-purple"
    },
    {
      icon: Server,
      title: "Tenant Engineering",
      description: "Expert configuration and optimization of your Microsoft 365 tenant, including identity management, access controls, and service deployment.",
      modalTitle: "M365 Tenant Engineering",
      modalDescription: "Our tenant engineering services provide expert configuration and optimization of your Microsoft 365 environment. Contact us to discuss your needs.",
      iconBgColor: "bg-blue-50",
      iconColor: "text-blue-600"
    },
    {
      icon: Code,
      title: "Automation & Integration",
      description: "Custom workflows, PowerApps, and integrations that streamline processes and connect Microsoft 365 with your existing systems.",
      modalTitle: "M365 Automation & Integration",
      modalDescription: "Our automation and integration services help you streamline processes and connect Microsoft 365 with your existing systems. Fill out the form to learn more.",
      iconBgColor: "bg-green-50",
      iconColor: "text-green-600"
    }
  ];

  return (
    <section className="py-16" id="services">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <ServiceCard 
              key={index}
              icon={service.icon}
              title={service.title}
              description={service.description}
              modalTitle={service.modalTitle}
              modalDescription={service.modalDescription}
              iconBgColor={service.iconBgColor}
              iconColor={service.iconColor}
            />
          ))}
        </div>
      </div>
    </section>
  );
};
